import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useIndicatorValues } from '~pages/LiveMap/Timeline/components/hooks/useIndicatorValues';
import { TimeIndicatorContextProvider } from '~pages/LiveMap/Timeline/components/hooks/useTimeIndicatorContext';
import { useTimeNormalization } from '~pages/LiveMap/Timeline/components/hooks/useTimeNormalization';
import TimeIndicator from '~pages/LiveMap/Timeline/components/TimeIndicator';

import {
  TimelineTickLabels,
  TimelineTickLines,
  TimelineTimeDisplay,
} from '../../Distances/DistanceMap/timelineTicks';

const GRAPH_HEIGHT = 32;

interface TimelineCoreProps {
  pings: LocationHistoryMinimal[];
  onTimeChange?: (time: number) => void;
}

function TimelineCore({ pings, onTimeChange }: TimelineCoreProps) {
  const theme = useTheme();
  const firstPingTime = pings[0].occurredAt;
  const lastPingTime = pings[pings.length - 1].occurredAt;
  const startMs = firstPingTime.valueOf();
  const endMs = lastPingTime.valueOf();
  const minTime = firstPingTime.toISOString();
  const maxTime = lastPingTime.toISOString();

  const normalize = useTimeNormalization(minTime, maxTime);
  const { graphRef, indicatorValues } = useIndicatorValues(minTime, maxTime, []);

  useEffect(() => {
    if (indicatorValues.indicatorTime && onTimeChange) {
      onTimeChange(indicatorValues.indicatorTime);
    }
  }, [indicatorValues.indicatorTime, onTimeChange]);

  return (
    <>
      <Box sx={{ mb: -1 }}>
        <TimelineTimeDisplay indicatorTime={indicatorValues.indicatorTime} label="" />
      </Box>

      <Box>
        <Box
          ref={graphRef}
          sx={{
            position: 'relative',
            height: GRAPH_HEIGHT,
            border: `1px solid ${theme.brandV2.colors.treadGray6}`,
            borderRadius: theme.brandV2.borderRadius,
            backgroundColor: theme.semantic.surfaceRaised,
            overflow: 'hidden',
          }}
        >
          <TimeIndicator
            visible={indicatorValues.indicatorVisible}
            position={indicatorValues.indicatorPosition}
            time={indicatorValues.indicatorTime}
          />

          <TimelineTickLines startMs={startMs} endMs={endMs} normalize={normalize} />
        </Box>

        <TimelineTickLabels startMs={startMs} endMs={endMs} normalize={normalize} />
      </Box>
    </>
  );
}

interface GeofenceTimelineProps {
  pings: LocationHistoryMinimal[];
  onTimeChange?: (time: number) => void;
}

export function GeofenceTimeline({ pings, onTimeChange }: GeofenceTimelineProps) {
  if (pings.length < 2) return null;

  return (
    <TimeIndicatorContextProvider>
      <TimelineCore pings={pings} onTimeChange={onTimeChange} />
    </TimeIndicatorContextProvider>
  );
}
